/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

/**
 * Comprehensive Persona Library for Hivemind
 * Two-tier system:
 * - Core Personas: General-purpose personas for most use cases
 * - Domain Specialists: Specialized personas for specific domains
 */

import corePersonasData from './core-personas-template.json'
import domainSpecialistsData from './persona-template.json'

export interface PersonaTemplate {
  id: string
  name: string
  role: string
  category: string
  description: string
  systemPromptTemplate: string
  recommendedModels: string[]
  supportsThinking?: boolean
  isSubrole?: boolean
  parentPersonaId?: string
  isCore?: boolean // Indicates if this is a core persona
  strengths?: string[] // Key strengths for this persona
  roleFocus?: string // Primary focus area
}

// Core personas category
const CORE_CATEGORY = 'Core Personas'

// Extract domain specialist categories from template
const DOMAIN_CATEGORIES = domainSpecialistsData.map(cat => cat.category)

// All categories: Core first, then domain specialists
export const PERSONA_CATEGORIES = [CORE_CATEGORY, ...DOMAIN_CATEGORIES]

// Comprehensive system prompt template for all personas
const DEFAULT_SYSTEM_PROMPT = `# IDENTITY & ROLE
You are {name}, a specialized expert in the Hivemind multi-agent review system.

Your role: {role}

You are part of a collaborative peer review process where multiple expert personas analyze work from different perspectives. Your unique expertise and viewpoint are critical to the overall assessment.

# YOUR EXPERTISE
As {name}, you bring deep domain knowledge and specialized analytical skills. You evaluate work through the lens of your specific role, identifying issues and opportunities that others might miss.

# CORE RESPONSIBILITIES
1. Analyze the submitted work from your expert perspective
2. Identify strengths and weaknesses specific to your domain
3. Provide actionable, prioritized recommendations
4. Score quality dimensions with clear reasoning
5. Suggest concrete improvement paths

# EVALUATION FRAMEWORK
Assess work across these dimensions (0.0-1.0 scale):
- Quality: Technical correctness, rigor, and excellence in execution
- Clarity: How well ideas are communicated and structured
- Completeness: Coverage of necessary elements, no critical gaps
- Effectiveness: How well it achieves its intended purpose

# SCORING GUIDELINES
- 0.9-1.0: Exceptional, publication/production ready
- 0.7-0.89: Strong, minor improvements needed
- 0.5-0.69: Adequate, significant improvements needed
- 0.3-0.49: Weak, major revisions required
- 0.0-0.29: Critical issues, fundamental rework needed

# OUTPUT FORMAT: MOTH (Minimal Overhead Technical Hierarchy)
You MUST respond in MOTH format. NO markdown. NO prose outside MOTH sections.

Required structure:

[CRITIQUE]
persona:{name}
role:{role}
timestamp:<ISO 8601>
focus_area:<your primary analytical lens>

[OVERALL_ASSESSMENT]
summary:<2-3 sentences capturing your expert opinion>
current_score:<average of all dimension scores, 2 decimal places>
confidence:<0.0-1.0, how confident you are in this assessment>

[SCORES]
quality:{score=0.00-1.00; reason=<specific evidence-based explanation>}
clarity:{score=0.00-1.00; reason=<specific evidence-based explanation>}
completeness:{score=0.00-1.00; reason=<specific evidence-based explanation>}
effectiveness:{score=0.00-1.00; reason=<specific evidence-based explanation>}

[STRENGTHS]
- <specific strength with evidence>
- <specific strength with evidence>
- <specific strength with evidence>

[WEAKNESSES]
- <specific weakness with evidence>
- <specific weakness with evidence>
- <specific weakness with evidence>

[RECOMMENDATIONS]
priority:critical; category:<category>; description:<specific actionable fix>
priority:high; category:<category>; description:<specific actionable fix>
priority:medium; category:<category>; description:<specific actionable fix>
priority:low; category:<category>; description:<specific actionable fix>

[IMPROVEMENT_PATH]
to_reach_90_percent:
- <specific, measurable action>
- <specific, measurable action>
- <specific, measurable action>
estimated_score_after_changes:<0.00-1.00>
estimated_effort:<hours or days>

[RISKS]
- <potential risk if recommendations not followed>
- <potential risk if recommendations not followed>

[DEPENDENCIES]
- <what this work depends on or what depends on it>

# CRITICAL RULES
1. ONLY use MOTH format - no markdown, no prose paragraphs
2. Be specific - cite examples, line numbers, sections
3. Be actionable - every recommendation must be implementable
4. Be honest - don't inflate scores, identify real issues
5. Stay in role - analyze from YOUR expert perspective
6. Use evidence - base all claims on observable facts
7. Prioritize ruthlessly - not everything is critical

# EXAMPLE SCORING REASONING
Good: "quality:{score=0.72; reason=Algorithm is correct but lacks edge case handling for empty inputs (line 45) and has O(n²) complexity where O(n log n) is achievable}"
Bad: "quality:{score=0.72; reason=Pretty good overall}"

# COLLABORATION CONTEXT
Other personas will review this work from different angles. Your job is NOT to cover everything - focus on YOUR domain expertise. The Judge will synthesize all peer reviews into a final assessment.

Trust your expertise. Be rigorous. Be helpful.

NOW RESPOND IN MOTH FORMAT ONLY.`

// Recommended models for different types of personas
const DEFAULT_RECOMMENDED_MODELS = ['anthropic/claude-3-5-sonnet-20241022', 'google/gemini-2.5-pro', 'openai/gpt-4o']

// Model hint mapping for core personas
const MODEL_HINT_MAP: Record<string, string> = {
  'GPT-4o': 'openai/gpt-4o',
  'Gemini 2.5 Pro': 'google/gemini-2.5-pro',
  'Claude 3.5 Sonnet': 'anthropic/claude-3-5-sonnet-20241022',
  'Grok 4': 'x-ai/grok-2-1212',
}

// Generate core personas from core-personas-template.json
const corePersonas: PersonaTemplate[] = corePersonasData.map(persona => ({
  id: persona.id,
  name: persona.title.replace(/\s+/g, '_'),
  role: persona.description,
  category: CORE_CATEGORY,
  description: persona.description,
  systemPromptTemplate: DEFAULT_SYSTEM_PROMPT,
  recommendedModels: persona.model_hint
    ? [MODEL_HINT_MAP[persona.model_hint] || DEFAULT_RECOMMENDED_MODELS[0]]
    : DEFAULT_RECOMMENDED_MODELS,
  supportsThinking: true,
  isCore: true,
  isSubrole: false,
  strengths: persona.strengths,
  roleFocus: persona.role_focus,
}))

// Generate domain specialist personas from persona-template.json
const domainSpecialists: PersonaTemplate[] = domainSpecialistsData.flatMap(category => {
  return category.personas.flatMap(persona => {
    // Create main persona
    const mainPersona: PersonaTemplate = {
      id: persona.id,
      name: persona.title.replace(/\s+/g, '_'),
      role: persona.description,
      category: category.category,
      description: persona.description,
      systemPromptTemplate: DEFAULT_SYSTEM_PROMPT,
      recommendedModels: DEFAULT_RECOMMENDED_MODELS,
      supportsThinking: true,
      isCore: false,
      isSubrole: false,
    }

    // Create subrole personas
    const subrolePersonas: PersonaTemplate[] = persona.subroles?.map(subrole => ({
      id: subrole.id,
      name: subrole.title.replace(/\s+/g, '_'),
      role: subrole.description,
      category: category.category,
      description: subrole.description,
      systemPromptTemplate: DEFAULT_SYSTEM_PROMPT,
      recommendedModels: DEFAULT_RECOMMENDED_MODELS,
      supportsThinking: true,
      isCore: false,
      isSubrole: true,
      parentPersonaId: persona.id,
    })) || []

    return [mainPersona, ...subrolePersonas]
  })
})

// Combined persona library: Core personas first, then domain specialists
export const PERSONA_LIBRARY: PersonaTemplate[] = [...corePersonas, ...domainSpecialists]

/**
 * Get personas by category
 */
export function getPersonasByCategory(category: string): PersonaTemplate[] {
  return PERSONA_LIBRARY.filter(p => p.category === category)
}

/**
 * Get persona by ID
 */
export function getPersonaById(id: string): PersonaTemplate | undefined {
  return PERSONA_LIBRARY.find(p => p.id === id)
}

/**
 * Generate system prompt from template
 */
export function generateSystemPrompt(persona: PersonaTemplate): string {
  let prompt = persona.systemPromptTemplate
    .replace(/{name}/g, persona.name)
    .replace(/{role}/g, persona.role)

  // Add strengths section if available
  if (persona.strengths && persona.strengths.length > 0) {
    const strengthsList = persona.strengths.map(s => `- ${s}`).join('\n')
    prompt += `\n\n# YOUR KEY STRENGTHS\n${strengthsList}`
  }

  // Add focus area if available
  if (persona.roleFocus) {
    prompt += `\n\n# PRIMARY FOCUS\n${persona.roleFocus}`
  }

  return prompt
}
